import awsconfig from '@/amplifyconfiguration.json';
import { ErrorDialog } from '@/features/misc/ErrorDialog';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Amplify } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import { executeGetAtAllResponse } from '@/client/rest';

import {
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  Link,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useGetPatient } from '../hooks/useGetPatient';
import { TemporaryRegistrationType } from '../types/TemporaryRegistrationType';
import { PageName } from '../index';
import { PrivacyPolicy } from './PrivacyPolicy';
import { TermsOfService } from './TermsOfService';
import { useCognitoAuth } from '@/hooks/useCognitoAuth';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';

Amplify.configure(awsconfig);

const schema: yup.ObjectSchema<TemporaryRegistrationType> = yup.object({
  email: yup
    .string()
    .required('メールアドレスを入力してください。')
    .email('メールアドレスを正しい形式で入力してください') // @の判定のみ
    .test('is-valid-email', 'メールアドレスを正しい形式で入力してください', (value) => {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(value);
    }),
  password: yup
    .string()
    .required('パスワードを入力してください。')
    .test('password-complexity', 'パスワードの複雑さが不十分です', function (value) {
      const errors = passwordValidation(value);
      if (errors) {
        return this.createError({ message: errors.join('') });
      }
      return true;
    }),
  passwordConfirm: yup
    .string()
    .oneOf([yup.ref('password'), undefined], '同じパスワードを入力してください。')
    .required('パスワードを入力してください。'),
  agreement: yup.bool().required('同意が必要です。').default(false).isTrue('同意が必要です。')
});

const passwordValidation = (value: string) => {
  const errors = [];

  // 8文字以上のチェック
  if (value.length < 8) {
    errors.push('パスワードは8文字以上を入力してください。');
  }
  if (!/[a-z]/.test(value)) {
    errors.push('小文字を1文字以上含めてください。');
  }
  if (!/[A-Z]/.test(value)) {
    errors.push('大文字を1文字以上含めてください。');
  }
  if (!/\d/.test(value)) {
    errors.push('数字を1文字以上含めてください。');
  }
  if (!/[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]/.test(value)) {
    errors.push('記号(^ $ * . [ ] { } ( ) ? - " ! @ # % & /  , > < \' : ; | _ ~ ` + =)を1文字以上含めてください。');
  }
  return errors.length ? errors : null;
};

export function SignUpForm(props: any) {
  const { handleSignUp, hasError } = useCognitoAuth();
  const methods = useForm<TemporaryRegistrationType>({
    defaultValues: {
      email: '',
      password: '',
      passwordConfirm: '',
      agreement: false
    },
    resolver: yupResolver(schema)
  });
  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues
  } = methods;

  const { loading } = useGetPatient();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [backdropOpen, setBackdropOpen] = useState<boolean>(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState<string>('');
  const [agreementDialogOpen, setAgreementDialogOpen] = useState<boolean>(false);

  async function onSubmit() {
    setConfirmDialogOpen(true);
  }

  async function isLineUserAlready() {
    try {
      const res = await executeGetAtAllResponse({
        apiName: 'medirecoWebApi',
        path: '/is-line-user-transfered',
        options: {
          queryParams: {
            accessToken: props.accessToken
          }
        }
      });

      if (res.statusCode === 200) {
        const responseBody = await res.body.json();
        return responseBody;
      }
    } catch (error) {
      if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
        Sentry.captureException(error);
      }

      console.log('error:', error);
      setErrorDialogMessage('lineのUserの重複確認に失敗しました。\n');
    }
  }

  async function isEmailUserAlready() {
    // 既にemailが登録されれいればtrue, なければfalseを返す
    // サインインプションにusernameとemailの両方があると、amplifyのsignUpメソッドの重複確認が使えないため手作り
    const formValues = getValues();
    const email = formValues.email;
    try {
      const res = await executeGetAtAllResponse({
        apiName: 'medirecoWebApi',
        path: '/is-email-registered',
        options: {
          queryParams: {
            email: email
          }
        }
      });

      if (res.statusCode === 200) {
        const isEmailExist = await res.body.json();
        return isEmailExist; // trueかfalse
      }
    } catch (error) {
      if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
        Sentry.captureException(error);
      }

      console.log('error:', error);
      setErrorDialogMessage('Email重複確認に失敗しました。\n');
    }
  }

  async function CheckUserAndSignUp() {
    setBackdropOpen(true);
    // リジェユーザの移行登録の際には、既に移行済みかどうかを判断する
    if (props.accessToken !== undefined) {
      // 既に該当するlineUserIdで登録処理がされていた場合はサインアップしない
      const isLineUserAlreadyTransfered = await isLineUserAlready();
      if (isLineUserAlreadyTransfered) {
        props.setActivePage(PageName.AlreadyRegistered);
        setBackdropOpen(false);
        return;
      }
    }
    // 該当するemailが既に登録されている場合はサインアップしない
    const isEmailAlreadyRegistered = await isEmailUserAlready();
    if (isEmailAlreadyRegistered) {
      props.setActivePage(PageName.AlreadyRegistered);
      setBackdropOpen(false);
      return;
    }
    // サインイン処理
    await handleSignUpSetEmail();
  }

  async function handleSignUpSetEmail() {
    const formValues = getValues();
    try {
      // エイリアスとしてユーザー名とemail両方を設定している場合username:emailとしてサインアップができないため
      // usernameにuuidを入れて登録処理を行っている
      const username = uuidv4();
      await handleSignUp(username, formValues.email, formValues.password);
      props.onUsernameSet(username);
      props.setActivePage(PageName.Token);
    } catch (e: any) {
      setErrorDialogMessage('お客様情報の保存に失敗しました。\n' + e.errors);
      props.setActivePage(PageName.AlreadyRegistered);
    } finally {
      setBackdropOpen(false);
    }
  }

  const ErrorMessage = (props: { message: string | undefined }) => {
    if (!props.message) return null;
    return (
      <Typography color="error" style={{ fontSize: '12px', marginLeft: '4px' }}>
        {props.message}
      </Typography>
    );
  };

  const BackDrop = (props: { isOpen: boolean }) => {
    return (
      <Backdrop
        sx={{ color: '#fff', backgroundColor: '#0003', zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={props.isOpen}
      >
        <CircularProgress color={'inherit'} />
      </Backdrop>
    );
  };

  const AgreementDialog = () => {
    return (
      <Dialog
        fullWidth
        open={agreementDialogOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <IconButton
          id="alert-dialog-title"
          data-gtm-dialog-title={'利用規約・プライバシーポリシー'}
          aria-label="close"
          onClick={() => {
            setAgreementDialogOpen(false);
          }}
          sx={(theme) => ({
            position: 'relative',
            padding: 0,
            marginLeft: 'auto',
            paddingRight: '8px',
            paddingTop: '8px',
            color: theme.palette.grey[500]
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ textAlign: 'center', backgroundColor: '#FFF', paddingBottom: '32px' }}>
          <br />
          <TermsOfService />
          <br />
          <br />
          <PrivacyPolicy />
        </DialogContent>
      </Dialog>
    );
  };

  const ConfirmDialog = () => {
    const ConfirmInformations = () => {
      const formValues = getValues();
      return (
        <Box>
          <Grid sx={{ textAlign: 'left' }} p={'0 24px'}>
            <Grid py={1} mx={0.5}>
              <Typography fontSize={18} color={'#000 !important'}>
                メールアドレス
              </Typography>
              <Typography fontSize={16}>{formValues.email}</Typography>
            </Grid>
            <Grid py={1} mx={0.5}>
              <Typography fontSize={18} color={'#000 !important'}>
                パスワード
              </Typography>
              <Typography fontSize={16}>
                {formValues.password
                  .split('')
                  .map(() => '●')
                  .join('')}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      );
    };

    const ConfirmButton = () => {
      return (
        <button
          type="button"
          style={{ width: '240px', height: '40px', color: '#FFFFFF', textAlign: 'center', margin: 'auto' }}
          className="bg-primary flex relative rounded"
          disabled={!!!confirmDialogOpen}
          onClick={() => CheckUserAndSignUp()}
          autoFocus
        >
          <div className="text-md m-auto">{'上記の内容で確定する'}</div>
        </button>
      );
    };

    return (
      <Dialog
        open={confirmDialogOpen}
        fullWidth
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <IconButton
          aria-label="close"
          onClick={() => {
            setConfirmDialogOpen(false);
          }}
          sx={(theme) => ({
            position: 'relative',
            padding: 0,
            marginLeft: 'auto',
            paddingRight: '8px',
            paddingTop: '8px',
            color: theme.palette.grey[500]
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ textAlign: 'center', backgroundColor: '#FFF', paddingBottom: '32px' }}>
          <DialogTitle
            fontSize={18}
            className="font-bold"
            id="alert-dialog-title"
            data-gtm-dialog-title={'登録内容確認'}
            sx={{ padding: 0, marginBottom: '16px' }}
          >
            登録内容確認
          </DialogTitle>
          <ConfirmInformations />
          <DialogActions>
            <ConfirmButton />
          </DialogActions>
        </DialogContent>
      </Dialog>
    );
  };

  const SubmitButton = () => {
    return (
      <button
        type="submit"
        style={{ width: '100%', height: '48px', color: '#FFFFFF', textAlign: 'center', margin: 'auto' }}
        className="bg-primary flex relative rounded"
      >
        <div className="text-md m-auto">{'登録内容を確認する'}</div>
      </button>
    );
  };

  // 患者情報の登録済チェック完了まではローディングのみ表示
  if (loading) {
    return <BackDrop isOpen={loading} />;
  }

  return (
    <FormProvider {...methods}>
      <Box sx={{ p: 2 }}>
        <Grid container direction={'row'} justifyContent={'center'} spacing={2}>
          <Grid item xs={12} sm={8}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container direction={'column'} spacing={2}>
                <Grid item xs={12}>
                  <Typography fontSize={14} mb={0.5}>
                    以下のお客様情報をご入力お願いいたします。
                  </Typography>
                </Grid>

                {/* メールアドレス入力フィールド */}
                <Grid item xs={12}>
                  <Stack direction={'column'}>
                    <InputLabel className="font-bold">
                      <Box fontSize={14} ml={0.5} color={'#424242'}>
                        メールアドレス
                      </Box>
                    </InputLabel>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <TextField
                            {...field}
                            type="email"
                            placeholder="example@healthinterview.clinic"
                            fullWidth
                            size="small"
                            sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                            error={!!error}
                          />
                          {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                        </>
                      )}
                    />
                  </Stack>
                </Grid>
                {/* パスワード */}
                <Grid item xs={12}>
                  <Stack direction={'column'}>
                    <InputLabel className="font-bold">
                      <Box fontSize={14} ml={0.5} color={'#424242'}>
                        パスワード
                      </Box>
                    </InputLabel>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <TextField
                            {...field}
                            type="password"
                            placeholder="8文字以上（大文字・小文字・数字・記号を含む）"
                            fullWidth
                            size="small"
                            sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                            error={!!error}
                          />
                          {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                        </>
                      )}
                    />
                  </Stack>
                </Grid>
                {/* パスワード確かめ */}
                <Grid item xs={12}>
                  <Stack direction={'column'}>
                    <InputLabel className="font-bold">
                      <Box fontSize={14} ml={0.5} color={'#424242'}>
                        パスワード(確認用)
                      </Box>
                    </InputLabel>
                    <Controller
                      name="passwordConfirm"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <TextField
                            {...field}
                            type="password"
                            placeholder="8文字以上（大文字・小文字・数字・記号を含む）"
                            fullWidth
                            size="small"
                            sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                            error={!!error}
                          />
                          {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                        </>
                      )}
                    />
                  </Stack>
                </Grid>

                {/* 同意チェック */}
                <Grid item xs={12}>
                  <Grid container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={1}>
                    <Controller
                      name="agreement"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          checked={field.value}
                          sx={{ paddingLeft: 0 }}
                          onClick={() => field.onChange(!field.value)}
                        />
                      )}
                    />
                    <Typography fontSize={14}>
                      <Link
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setAgreementDialogOpen(true);
                        }}
                      >
                        利用規約・プライバシーポリシー
                      </Link>
                      に同意する
                    </Typography>
                  </Grid>
                  <ErrorMessage message={errors.agreement?.message} />
                </Grid>

                {/* 送信ボタン */}
                <Grid item sx={{ mt: 2 }}>
                  <SubmitButton />
                </Grid>
              </Grid>
            </form>
          </Grid>

          <AgreementDialog />
          <ConfirmDialog />

          <ErrorDialog
            open={errorDialogOpen}
            title={'お客様情報登録エラー'}
            buttonLabel="閉じる"
            buttonAction={() => {
              setErrorDialogOpen(false);
            }}
          >
            {errorDialogMessage}
          </ErrorDialog>
        </Grid>
        <BackDrop isOpen={backdropOpen} />
      </Box>
    </FormProvider>
  );
}
